/**
 * Lab-admin i18n — FR default + AR-RTL. Registered once in the module's onRegister(). Keys are
 * namespaced under `admin.*` and `nav.admin.*` / `nav.section.administration`. Layout mirrors
 * automatically under dir="rtl" via CSS logical properties; codes / amounts / dates stay LTR with
 * .nv-ltr-island. Missing AR key → the FR value → the raw key (never a crash, never a blank).
 */
import { registerMessages } from "@/app";

const FR: Record<string, string> = {
  // ── nav ──────────────────────────────────────────────────────────────────────
  "nav.section.administration": "Administration",
  "nav.admin.overview": "Tableau de bord",
  "nav.admin.profile": "Profil du laboratoire",
  "nav.admin.team": "Équipe & permissions",
  "nav.admin.journal": "Journal d'audit",
  "nav.admin.pricing": "Tarification des analyses",
  "nav.admin.subscription": "Abonnement",

  // ── shared ─────────────────────────────────────────────────────────────────────
  "admin.action.refresh": "Actualiser",
  "admin.action.save": "Enregistrer",
  "admin.action.cancel": "Annuler",
  "admin.action.edit": "Modifier",
  "admin.action.close": "Fermer",
  "admin.action.confirm": "Confirmer",
  "admin.field.required": "Champ requis.",
  "admin.field.invalidEmail": "Adresse e-mail invalide.",
  "admin.field.invalidAmount": "Montant invalide.",
  "admin.toast.saved": "Modifications enregistrées.",
  "admin.toast.error": "L'opération a échoué.",
  "admin.governance.readonly": "Champ géré par Pharmakon (lecture seule).",
  "admin.empty.none": "Aucune donnée.",

  // ── 1. Overview (Tableau de bord) ───────────────────────────────────────────────
  "admin.overview.title": "Tableau de bord",
  "admin.overview.subtitle": "Vue d'ensemble de la gouvernance du laboratoire.",
  "admin.overview.kpi.staff": "Membres de l'équipe",
  "admin.overview.kpi.staff.hint": "Comptes actifs du laboratoire",
  "admin.overview.kpi.owners": "Propriétaires",
  "admin.overview.kpi.priced": "Analyses tarifées",
  "admin.overview.kpi.priced.hint": "Codes avec un prix attribué",
  "admin.overview.kpi.plan": "Plan",
  "admin.overview.kpi.statut": "Statut d'abonnement",
  "admin.overview.kpi.audit": "Événements (page)",
  "admin.overview.kpi.audit.hint": "Entrées du journal d'audit",
  "admin.overview.health.title": "Santé de l'abonnement",
  // NOVA-063 — master-data sync health (tile + perime banner)
  "admin.overview.sync.title": "Synchronisation des données",
  "admin.overview.sync.kpi": "État de synchronisation",
  "admin.overview.sync.etat.frais": "À jour",
  "admin.overview.sync.etat.en_grace": "En période de grâce",
  "admin.overview.sync.etat.perime": "Périmé",
  "admin.overview.sync.lastSync": "Dernière synchronisation",
  "admin.overview.sync.lastSync.never": "Jamais synchronisé",
  "admin.overview.sync.graceExpire": "Fin de la période de grâce",
  "admin.overview.sync.version": "Version des données",
  "admin.overview.sync.perime.title": "Données de référence périmées",
  "admin.overview.sync.perime.desc":
    "Le catalogue n'a pas été synchronisé depuis plus de {graceDays} jours. Les modules cloud payants sont suspendus ; les soins et la facturation locale restent disponibles. Vérifiez la connexion à l'agent.",
  "admin.overview.sync.grace.desc":
    "Hors ligne mais dans la période de grâce — la synchronisation reprendra automatiquement au retour de la connexion.",
  "admin.overview.quick.title": "Accès rapides",
  "admin.overview.quick.profile": "Modifier le profil",
  "admin.overview.quick.team": "Gérer l'équipe",
  "admin.overview.quick.journal": "Consulter le journal",
  "admin.overview.quick.pricing": "Tarifier les analyses",
  "admin.overview.quick.subscription": "Voir l'abonnement",
  "admin.overview.restricted": "Certaines sections sont masquées faute de droits.",

  // ── 2. Profile (Profil du laboratoire) ──────────────────────────────────────────
  "admin.profile.title": "Profil du laboratoire",
  "admin.profile.subtitle": "Identité, coordonnées et identifiants fiscaux du laboratoire.",
  "admin.profile.section.identity": "Identité",
  "admin.profile.section.fiscal": "Identifiants fiscaux",
  "admin.profile.section.contact": "Coordonnées",
  "admin.profile.section.governance": "Gouvernance",
  "admin.profile.raison_sociale": "Raison sociale",
  "admin.profile.nom_commercial": "Nom commercial",
  "admin.profile.nif": "NIF",
  "admin.profile.nis": "NIS",
  "admin.profile.rc": "Registre de commerce (RC)",
  "admin.profile.art": "Article d'imposition",
  "admin.profile.wilaya": "Wilaya",
  "admin.profile.commune": "Commune",
  "admin.profile.adresse": "Adresse",
  "admin.profile.telephone": "Téléphone",
  "admin.profile.email": "E-mail",
  "admin.profile.reference": "Référence",
  "admin.profile.deploy_class": "Classe de déploiement",
  "admin.profile.tenancy_tier": "Palier de tenancy",
  "admin.profile.edit": "Modifier le profil",
  "admin.profile.saved": "Profil enregistré.",
  "admin.profile.empty.title": "Profil indisponible",
  "admin.profile.empty.desc": "Le profil du laboratoire n'a pas pu être chargé.",
  "admin.profile.dirty.required": "Modifiez au moins un champ avant d'enregistrer.",

  // ── 3. Team & permissions (Équipe & permissions) ────────────────────────────────
  "admin.team.title": "Équipe & permissions",
  "admin.team.subtitle": "Comptes, rôles préconfigurés et droits fins (les refus l'emportent).",
  "admin.team.new": "Nouveau membre",
  "admin.team.col.name": "Nom",
  "admin.team.col.email": "E-mail",
  "admin.team.col.presets": "Rôles",
  "admin.team.col.owner": "Propriétaire",
  "admin.team.col.statut": "Statut",
  "admin.team.col.lastLogin": "Dernière connexion",
  "admin.team.col.actions": "Actions",
  "admin.team.owner.yes": "Propriétaire",
  "admin.team.owner.no": "Membre",
  "admin.team.statut.actif": "Actif",
  "admin.team.statut.inactif": "Inactif",
  "admin.team.statut.suspendu": "Suspendu",
  "admin.team.statut.archive": "Archivé",
  // NOVA-031 — staff lifecycle (suspend / revoke / archive)
  "admin.team.lifecycle.suspend": "Suspendre",
  "admin.team.lifecycle.revoke": "Révoquer",
  "admin.team.lifecycle.archive": "Archiver",
  "admin.team.lifecycle.suspend.title": "Suspendre le membre",
  "admin.team.lifecycle.suspend.body":
    "Suspendre {name} ? Le compte ne pourra plus se connecter et perd immédiatement ses droits. La suspension est réversible.",
  "admin.team.lifecycle.suspend.confirm": "Suspendre",
  "admin.team.lifecycle.suspend.done": "Membre suspendu.",
  "admin.team.lifecycle.revoke.title": "Révoquer l'affiliation",
  "admin.team.lifecycle.revoke.body":
    "Révoquer l'affiliation de {name} ? L'accès au laboratoire est retiré et la session est fermée. Le compte ne pourra plus se connecter.",
  "admin.team.lifecycle.revoke.confirm": "Révoquer",
  "admin.team.lifecycle.revoke.done": "Affiliation révoquée.",
  "admin.team.lifecycle.archive.title": "Archiver le compte",
  "admin.team.lifecycle.archive.body":
    "Archiver {name} ? Action terminale : le compte est désactivé définitivement, l'affiliation révoquée et la session fermée.",
  "admin.team.lifecycle.archive.confirm": "Archiver",
  "admin.team.lifecycle.archive.done": "Compte archivé.",
  "admin.team.empty.title": "Aucun membre",
  "admin.team.empty.desc": "Invitez votre premier membre pour constituer l'équipe.",
  "admin.team.manage": "Permissions",
  "admin.team.preset.assistant": "Assistant·e",
  "admin.team.preset.biologiste": "Biologiste",
  "admin.team.preset.automate_agent": "Agent automate",
  "admin.team.preset.manager": "Manager",
  "admin.team.preset.comptable": "Comptable",
  "admin.team.preset.magasinier": "Magasinier",
  "admin.team.preset.preleveur": "Préleveur",
  "admin.team.preset.caissier": "Caissier",

  // create-staff modal
  "admin.team.create.title": "Nouveau membre",
  "admin.team.create.nom": "Nom",
  "admin.team.create.prenom": "Prénom",
  "admin.team.create.email": "E-mail",
  "admin.team.create.password": "Mot de passe initial",
  "admin.team.create.presets": "Rôles préconfigurés",
  "admin.team.create.owner": "Désigner comme propriétaire",
  "admin.team.create.submit": "Créer le compte",
  "admin.team.create.done": "Compte créé.",

  // membership / permissions drawer
  "admin.team.perms.title": "Permissions — {name}",
  "admin.team.perms.presets": "Rôles préconfigurés",
  "admin.team.perms.presets.hint": "Les bundles de capacités attribués à ce membre.",
  "admin.team.perms.owner": "Propriétaire du laboratoire",
  "admin.team.perms.owner.hint": "Accès complet — y compris la trilogie réservée au propriétaire.",
  "admin.team.perms.savePreset": "Mettre à jour les rôles",
  "admin.team.perms.presetSaved": "Rôles mis à jour.",
  "admin.team.perms.grants": "Droits fins (dérogations)",
  "admin.team.perms.grants.hint":
    "Surcouches au-delà des rôles. Un refus l'emporte toujours sur un octroi (deny-wins).",
  "admin.team.perms.grant.add": "Ajouter une dérogation",
  "admin.team.perms.grant.capability": "Capacité",
  "admin.team.perms.grant.capability.placeholder": "ex. finance.facture.issue",
  "admin.team.perms.grant.effet": "Effet",
  "admin.team.perms.grant.effet.grant": "Octroi",
  "admin.team.perms.grant.effet.deny": "Refus (prioritaire)",
  "admin.team.perms.grant.motif": "Motif (facultatif)",
  "admin.team.perms.grant.submit": "Ajouter",
  "admin.team.perms.grant.added": "Dérogation ajoutée.",
  "admin.team.perms.grant.remove": "Retirer",
  "admin.team.perms.grant.removed": "Dérogation retirée.",
  "admin.team.perms.grant.none": "Aucune dérogation. Les droits découlent des rôles ci-dessus.",
  "admin.team.perms.grant.col.capability": "Capacité",
  "admin.team.perms.grant.col.effet": "Effet",
  "admin.team.perms.grant.col.motif": "Motif",
  "admin.team.perms.grant.col.created": "Ajoutée le",
  "admin.team.perms.denyWins": "Refus prioritaire",
  "admin.team.perms.grantTone": "Octroi",

  // ── 4. Audit journal (Journal d'audit) ──────────────────────────────────────────
  "admin.journal.title": "Journal d'audit",
  "admin.journal.subtitle": "Traçabilité des actions du laboratoire (métadonnées uniquement).",
  "admin.journal.filter.entite": "Entité",
  "admin.journal.filter.action": "Action",
  "admin.journal.filter.actor": "Acteur (id)",
  "admin.journal.filter.capability": "Capacité",
  "admin.journal.filter.from": "Du",
  "admin.journal.filter.to": "Au",
  "admin.journal.filter.apply": "Filtrer",
  "admin.journal.filter.reset": "Réinitialiser",
  "admin.journal.col.at": "Horodatage",
  "admin.journal.col.entite": "Entité",
  "admin.journal.col.action": "Action",
  "admin.journal.col.actor": "Acteur",
  "admin.journal.col.capability": "Capacité",
  "admin.journal.col.details": "Détails",
  "admin.journal.details": "Voir",
  "admin.journal.empty.title": "Aucun événement",
  "admin.journal.empty.desc": "Aucune entrée ne correspond aux filtres.",
  "admin.journal.detail.title": "Événement d'audit",
  "admin.journal.detail.before": "Avant",
  "admin.journal.detail.after": "Après",
  "admin.journal.detail.context": "Contexte",
  "admin.journal.range.invalid": "La date « Du » doit précéder la date « Au ».",

  // ── 5. Pricing (Tarification des analyses) ──────────────────────────────────────
  "admin.pricing.title": "Tarification des analyses",
  "admin.pricing.subtitle": "Prix attribués aux types d'analyses (journal des prix immuable).",
  "admin.pricing.search": "Rechercher (code, libellé)…",
  "admin.pricing.assign": "Attribuer un prix",
  "admin.pricing.col.code": "Code",
  "admin.pricing.col.libelle": "Libellé",
  "admin.pricing.col.discipline": "Discipline",
  "admin.pricing.col.prix": "Prix",
  "admin.pricing.col.statut": "Statut",
  "admin.pricing.col.actions": "Actions",
  "admin.pricing.statut.actif": "Actif",
  "admin.pricing.statut.retire": "Retiré",
  "admin.pricing.empty.title": "Aucun prix attribué",
  "admin.pricing.empty.desc": "Attribuez un premier prix à un type d'analyse pour démarrer.",
  "admin.pricing.empty.search": "Aucun type d'analyse ne correspond à la recherche.",
  "admin.pricing.edit": "Modifier le prix",
  "admin.pricing.modal.title.new": "Attribuer un prix",
  "admin.pricing.modal.title.edit": "Modifier le prix — {code}",
  "admin.pricing.modal.code": "Code du type d'analyse",
  "admin.pricing.modal.code.placeholder": "Rechercher un code, libellé ou discipline…",
  // shared catalogue picker (NOVA-011/032)
  "catalogue.picker.searching": "Recherche…",
  "catalogue.picker.empty": "Aucun paramètre ne correspond",
  "catalogue.picker.error": "Échec de la recherche du catalogue",
  "admin.pricing.modal.prix": "Prix (DZD)",
  "admin.pricing.modal.motif": "Motif du changement",
  "admin.pricing.modal.motif.hint": "Obligatoire lors d'un changement de prix existant (journal des prix).",
  "admin.pricing.modal.submit": "Enregistrer le prix",
  "admin.pricing.saved.instantiated": "Prix attribué.",
  "admin.pricing.saved.changed": "Prix mis à jour.",
  "admin.pricing.motif.required": "Un motif est requis pour modifier un prix existant.",

  // ── 6. Subscription (Abonnement) ───────────────────────────────────────────────
  "admin.subscription.title": "Abonnement",
  "admin.subscription.subtitle": "Plan, statut, modules et santé des droits (projection centrale).",
  "admin.subscription.plan": "Plan",
  "admin.subscription.statut": "Statut",
  "admin.subscription.version": "Version de projection",
  "admin.subscription.projectedAt": "Dernière synchronisation",
  "admin.subscription.reason": "Motif",
  "admin.subscription.health.title": "Santé des droits",
  "admin.subscription.health.synced": "Synchronisé",
  "admin.subscription.health.notSynced": "Non synchronisé",
  "admin.subscription.health.active": "Actif",
  "admin.subscription.health.inactive": "Inactif",
  "admin.subscription.modules.title": "Modules",
  "admin.subscription.modules.cloud": "Modules cloud",
  "admin.subscription.modules.cloud.hint": "SKUs payants — suspendus si le statut n'est pas actif.",
  "admin.subscription.modules.local": "Modules locaux",
  "admin.subscription.modules.local.hint": "Bundles sur site — jamais suspendus.",
  "admin.subscription.modules.empty": "Aucun module dans cette catégorie.",
  "admin.subscription.notSynced.title": "Abonnement non synchronisé",
  "admin.subscription.notSynced.desc":
    "L'abonnement n'a pas encore été synchronisé depuis le central. Les fonctionnalités restent disponibles.",
  "admin.subscription.request.title": "Demande de changement",
  "admin.subscription.request.cta": "Demander un changement",
  "admin.subscription.request.type": "Type de demande",
  "admin.subscription.request.type.upgrade": "Montée en gamme",
  "admin.subscription.request.type.downgrade": "Descente en gamme",
  "admin.subscription.request.type.cancel": "Résiliation",
  "admin.subscription.request.plan": "Plan cible",
  "admin.subscription.request.plan.placeholder": "ex. pro, premium",
  "admin.subscription.request.cycle": "Cycle",
  "admin.subscription.request.cycle.mensuel": "Mensuel",
  "admin.subscription.request.cycle.annuel": "Annuel",
  "admin.subscription.request.motif": "Motif (facultatif)",
  "admin.subscription.request.submit": "Envoyer la demande",
  "admin.subscription.request.planRequired": "Un plan cible est requis pour une montée/descente en gamme.",
  "admin.subscription.request.queued": "Demande enregistrée — le central la traitera.",
  "admin.subscription.request.reused": "Demande déjà en file d'attente.",
  "admin.subscription.request.notEnabled":
    "Les demandes de changement d'abonnement ne sont pas disponibles en ligne sur cette installation. Contactez votre prestataire Pharmakon pour modifier votre plan.",
  "admin.subscription.request.note":
    "Le central Pharmakon décide ; aucun droit n'est modifié localement (aucune donnée patient n'est transmise).",
};

const AR: Record<string, string> = {
  // ── nav ──────────────────────────────────────────────────────────────────────
  "nav.section.administration": "الإدارة",
  "nav.admin.overview": "لوحة القيادة",
  "nav.admin.profile": "ملف المختبر",
  "nav.admin.team": "الفريق والصلاحيات",
  "nav.admin.journal": "سجل التدقيق",
  "nav.admin.pricing": "تسعير التحاليل",
  "nav.admin.subscription": "الاشتراك",

  // ── shared ─────────────────────────────────────────────────────────────────────
  "admin.action.refresh": "تحديث",
  "admin.action.save": "حفظ",
  "admin.action.cancel": "إلغاء",
  "admin.action.edit": "تعديل",
  "admin.action.close": "إغلاق",
  "admin.action.confirm": "تأكيد",
  "admin.field.required": "حقل مطلوب.",
  "admin.field.invalidEmail": "بريد إلكتروني غير صالح.",
  "admin.field.invalidAmount": "مبلغ غير صالح.",
  "admin.toast.saved": "تم حفظ التعديلات.",
  "admin.toast.error": "فشلت العملية.",
  "admin.governance.readonly": "حقل تديره Pharmakon (للقراءة فقط).",
  "admin.empty.none": "لا توجد بيانات.",

  // ── 1. Overview ──────────────────────────────────────────────────────────────
  "admin.overview.title": "لوحة القيادة",
  "admin.overview.subtitle": "نظرة عامة على حوكمة المختبر.",
  "admin.overview.kpi.staff": "أعضاء الفريق",
  "admin.overview.kpi.staff.hint": "الحسابات النشطة للمختبر",
  "admin.overview.kpi.owners": "المالكون",
  "admin.overview.kpi.priced": "التحاليل المُسعّرة",
  "admin.overview.kpi.priced.hint": "الرموز التي لها سعر محدد",
  "admin.overview.kpi.plan": "الخطة",
  "admin.overview.kpi.statut": "حالة الاشتراك",
  "admin.overview.kpi.audit": "الأحداث (الصفحة)",
  "admin.overview.kpi.audit.hint": "إدخالات سجل التدقيق",
  "admin.overview.health.title": "صحة الاشتراك",
  // NOVA-063 — master-data sync health
  "admin.overview.sync.title": "مزامنة البيانات",
  "admin.overview.sync.kpi": "حالة المزامنة",
  "admin.overview.sync.etat.frais": "محدّث",
  "admin.overview.sync.etat.en_grace": "في فترة السماح",
  "admin.overview.sync.etat.perime": "منتهي الصلاحية",
  "admin.overview.sync.lastSync": "آخر مزامنة",
  "admin.overview.sync.lastSync.never": "لم تتم المزامنة قط",
  "admin.overview.sync.graceExpire": "نهاية فترة السماح",
  "admin.overview.sync.version": "إصدار البيانات",
  "admin.overview.sync.perime.title": "بيانات مرجعية منتهية الصلاحية",
  "admin.overview.sync.perime.desc":
    "لم تتم مزامنة الكتالوج منذ أكثر من {graceDays} يوماً. وحدات السحابة المدفوعة معلّقة؛ تبقى الرعاية والفوترة المحلية متاحة. تحقّق من الاتصال بالوكيل.",
  "admin.overview.sync.grace.desc":
    "غير متصل لكن ضمن فترة السماح — ستُستأنف المزامنة تلقائياً عند عودة الاتصال.",
  "admin.overview.quick.title": "وصول سريع",
  "admin.overview.quick.profile": "تعديل الملف",
  "admin.overview.quick.team": "إدارة الفريق",
  "admin.overview.quick.journal": "عرض السجل",
  "admin.overview.quick.pricing": "تسعير التحاليل",
  "admin.overview.quick.subscription": "عرض الاشتراك",
  "admin.overview.restricted": "بعض الأقسام مخفية بسبب نقص الصلاحيات.",

  // ── 2. Profile ───────────────────────────────────────────────────────────────
  "admin.profile.title": "ملف المختبر",
  "admin.profile.subtitle": "هوية المختبر ومعلومات الاتصال والمعرّفات الجبائية.",
  "admin.profile.section.identity": "الهوية",
  "admin.profile.section.fiscal": "المعرّفات الجبائية",
  "admin.profile.section.contact": "معلومات الاتصال",
  "admin.profile.section.governance": "الحوكمة",
  "admin.profile.raison_sociale": "التسمية القانونية",
  "admin.profile.nom_commercial": "الاسم التجاري",
  "admin.profile.nif": "رقم التعريف الجبائي (NIF)",
  "admin.profile.nis": "رقم التعريف الإحصائي (NIS)",
  "admin.profile.rc": "السجل التجاري (RC)",
  "admin.profile.art": "مادة الضريبة",
  "admin.profile.wilaya": "الولاية",
  "admin.profile.commune": "البلدية",
  "admin.profile.adresse": "العنوان",
  "admin.profile.telephone": "الهاتف",
  "admin.profile.email": "البريد الإلكتروني",
  "admin.profile.reference": "المرجع",
  "admin.profile.deploy_class": "فئة النشر",
  "admin.profile.tenancy_tier": "مستوى الاستضافة",
  "admin.profile.edit": "تعديل الملف",
  "admin.profile.saved": "تم حفظ الملف.",
  "admin.profile.empty.title": "الملف غير متاح",
  "admin.profile.empty.desc": "تعذّر تحميل ملف المختبر.",
  "admin.profile.dirty.required": "عدّل حقلاً واحداً على الأقل قبل الحفظ.",

  // ── 3. Team ──────────────────────────────────────────────────────────────────
  "admin.team.title": "الفريق والصلاحيات",
  "admin.team.subtitle": "الحسابات والأدوار المُعدّة مسبقاً والصلاحيات الدقيقة (الرفض له الأولوية).",
  "admin.team.new": "عضو جديد",
  "admin.team.col.name": "الاسم",
  "admin.team.col.email": "البريد الإلكتروني",
  "admin.team.col.presets": "الأدوار",
  "admin.team.col.owner": "مالك",
  "admin.team.col.statut": "الحالة",
  "admin.team.col.lastLogin": "آخر دخول",
  "admin.team.col.actions": "إجراءات",
  "admin.team.owner.yes": "مالك",
  "admin.team.owner.no": "عضو",
  "admin.team.statut.actif": "نشط",
  "admin.team.statut.inactif": "غير نشط",
  "admin.team.statut.suspendu": "موقوف",
  "admin.team.statut.archive": "مؤرشف",
  // NOVA-031 — staff lifecycle
  "admin.team.lifecycle.suspend": "إيقاف",
  "admin.team.lifecycle.revoke": "إلغاء",
  "admin.team.lifecycle.archive": "أرشفة",
  "admin.team.lifecycle.suspend.title": "إيقاف العضو",
  "admin.team.lifecycle.suspend.body":
    "إيقاف {name}؟ لن يتمكن الحساب من تسجيل الدخول ويفقد صلاحياته فوراً. الإيقاف قابل للتراجع.",
  "admin.team.lifecycle.suspend.confirm": "إيقاف",
  "admin.team.lifecycle.suspend.done": "تم إيقاف العضو.",
  "admin.team.lifecycle.revoke.title": "إلغاء الانتساب",
  "admin.team.lifecycle.revoke.body":
    "إلغاء انتساب {name}؟ يُسحب الوصول إلى المختبر وتُغلق الجلسة. لن يتمكن الحساب من تسجيل الدخول.",
  "admin.team.lifecycle.revoke.confirm": "إلغاء",
  "admin.team.lifecycle.revoke.done": "تم إلغاء الانتساب.",
  "admin.team.lifecycle.archive.title": "أرشفة الحساب",
  "admin.team.lifecycle.archive.body":
    "أرشفة {name}؟ إجراء نهائي: يُعطّل الحساب نهائياً ويُلغى الانتساب وتُغلق الجلسة.",
  "admin.team.lifecycle.archive.confirm": "أرشفة",
  "admin.team.lifecycle.archive.done": "تمت أرشفة الحساب.",
  "admin.team.empty.title": "لا يوجد أعضاء",
  "admin.team.empty.desc": "ادعُ أول عضو لتكوين الفريق.",
  "admin.team.manage": "الصلاحيات",
  "admin.team.preset.assistant": "مساعد",
  "admin.team.preset.biologiste": "بيولوجي",
  "admin.team.preset.automate_agent": "وكيل الجهاز",
  "admin.team.preset.manager": "مدير",
  "admin.team.preset.comptable": "محاسب",
  "admin.team.preset.magasinier": "أمين المخزن",
  "admin.team.preset.preleveur": "آخذ العينات",
  "admin.team.preset.caissier": "أمين الصندوق",

  "admin.team.create.title": "عضو جديد",
  "admin.team.create.nom": "اللقب",
  "admin.team.create.prenom": "الاسم",
  "admin.team.create.email": "البريد الإلكتروني",
  "admin.team.create.password": "كلمة المرور الأولية",
  "admin.team.create.presets": "الأدوار المُعدّة مسبقاً",
  "admin.team.create.owner": "تعيينه مالكاً",
  "admin.team.create.submit": "إنشاء الحساب",
  "admin.team.create.done": "تم إنشاء الحساب.",

  "admin.team.perms.title": "الصلاحيات — {name}",
  "admin.team.perms.presets": "الأدوار المُعدّة مسبقاً",
  "admin.team.perms.presets.hint": "حزم القدرات المُسندة لهذا العضو.",
  "admin.team.perms.owner": "مالك المختبر",
  "admin.team.perms.owner.hint": "وصول كامل — بما في ذلك الثلاثية المحجوزة للمالك.",
  "admin.team.perms.savePreset": "تحديث الأدوار",
  "admin.team.perms.presetSaved": "تم تحديث الأدوار.",
  "admin.team.perms.grants": "صلاحيات دقيقة (استثناءات)",
  "admin.team.perms.grants.hint": "طبقات فوق الأدوار. الرفض يتغلّب دائماً على المنح (deny-wins).",
  "admin.team.perms.grant.add": "إضافة استثناء",
  "admin.team.perms.grant.capability": "القدرة",
  "admin.team.perms.grant.capability.placeholder": "مثال: finance.facture.issue",
  "admin.team.perms.grant.effet": "الأثر",
  "admin.team.perms.grant.effet.grant": "منح",
  "admin.team.perms.grant.effet.deny": "رفض (له الأولوية)",
  "admin.team.perms.grant.motif": "السبب (اختياري)",
  "admin.team.perms.grant.submit": "إضافة",
  "admin.team.perms.grant.added": "تمت إضافة الاستثناء.",
  "admin.team.perms.grant.remove": "إزالة",
  "admin.team.perms.grant.removed": "تمت إزالة الاستثناء.",
  "admin.team.perms.grant.none": "لا توجد استثناءات. الصلاحيات مستمدة من الأدوار أعلاه.",
  "admin.team.perms.grant.col.capability": "القدرة",
  "admin.team.perms.grant.col.effet": "الأثر",
  "admin.team.perms.grant.col.motif": "السبب",
  "admin.team.perms.grant.col.created": "أُضيفت في",
  "admin.team.perms.denyWins": "الرفض له الأولوية",
  "admin.team.perms.grantTone": "منح",

  // ── 4. Journal ───────────────────────────────────────────────────────────────
  "admin.journal.title": "سجل التدقيق",
  "admin.journal.subtitle": "تتبّع إجراءات المختبر (بيانات وصفية فقط).",
  "admin.journal.filter.entite": "الكيان",
  "admin.journal.filter.action": "الإجراء",
  "admin.journal.filter.actor": "الفاعل (المعرّف)",
  "admin.journal.filter.capability": "القدرة",
  "admin.journal.filter.from": "من",
  "admin.journal.filter.to": "إلى",
  "admin.journal.filter.apply": "تصفية",
  "admin.journal.filter.reset": "إعادة ضبط",
  "admin.journal.col.at": "الطابع الزمني",
  "admin.journal.col.entite": "الكيان",
  "admin.journal.col.action": "الإجراء",
  "admin.journal.col.actor": "الفاعل",
  "admin.journal.col.capability": "القدرة",
  "admin.journal.col.details": "التفاصيل",
  "admin.journal.details": "عرض",
  "admin.journal.empty.title": "لا توجد أحداث",
  "admin.journal.empty.desc": "لا يوجد إدخال يطابق عوامل التصفية.",
  "admin.journal.detail.title": "حدث تدقيق",
  "admin.journal.detail.before": "قبل",
  "admin.journal.detail.after": "بعد",
  "admin.journal.detail.context": "السياق",
  "admin.journal.range.invalid": "يجب أن يسبق تاريخ « من » تاريخ « إلى ».",

  // ── 5. Pricing ───────────────────────────────────────────────────────────────
  "admin.pricing.title": "تسعير التحاليل",
  "admin.pricing.subtitle": "الأسعار المُسندة لأنواع التحاليل (سجل أسعار غير قابل للتعديل).",
  "admin.pricing.search": "بحث (الرمز، التسمية)…",
  "admin.pricing.assign": "إسناد سعر",
  "admin.pricing.col.code": "الرمز",
  "admin.pricing.col.libelle": "التسمية",
  "admin.pricing.col.discipline": "التخصص",
  "admin.pricing.col.prix": "السعر",
  "admin.pricing.col.statut": "الحالة",
  "admin.pricing.col.actions": "إجراءات",
  "admin.pricing.statut.actif": "نشط",
  "admin.pricing.statut.retire": "مسحوب",
  "admin.pricing.empty.title": "لا يوجد سعر مُسند",
  "admin.pricing.empty.desc": "أسنِد أول سعر لنوع تحليل للبدء.",
  "admin.pricing.empty.search": "لا يوجد نوع تحليل يطابق البحث.",
  "admin.pricing.edit": "تعديل السعر",
  "admin.pricing.modal.title.new": "إسناد سعر",
  "admin.pricing.modal.title.edit": "تعديل السعر — {code}",
  "admin.pricing.modal.code": "رمز نوع التحليل",
  "admin.pricing.modal.code.placeholder": "ابحث برمز أو تسمية أو تخصص…",
  // shared catalogue picker (NOVA-011/032)
  "catalogue.picker.searching": "جارٍ البحث…",
  "catalogue.picker.empty": "لا يوجد معيار مطابق",
  "catalogue.picker.error": "فشل البحث في الكتالوج",
  "admin.pricing.modal.prix": "السعر (دج)",
  "admin.pricing.modal.motif": "سبب التغيير",
  "admin.pricing.modal.motif.hint": "إلزامي عند تغيير سعر موجود (سجل الأسعار).",
  "admin.pricing.modal.submit": "حفظ السعر",
  "admin.pricing.saved.instantiated": "تم إسناد السعر.",
  "admin.pricing.saved.changed": "تم تحديث السعر.",
  "admin.pricing.motif.required": "يلزم سبب لتعديل سعر موجود.",

  // ── 6. Subscription ──────────────────────────────────────────────────────────
  "admin.subscription.title": "الاشتراك",
  "admin.subscription.subtitle": "الخطة والحالة والوحدات وصحة الحقوق (الإسقاط المركزي).",
  "admin.subscription.plan": "الخطة",
  "admin.subscription.statut": "الحالة",
  "admin.subscription.version": "إصدار الإسقاط",
  "admin.subscription.projectedAt": "آخر مزامنة",
  "admin.subscription.reason": "السبب",
  "admin.subscription.health.title": "صحة الحقوق",
  "admin.subscription.health.synced": "متزامن",
  "admin.subscription.health.notSynced": "غير متزامن",
  "admin.subscription.health.active": "نشط",
  "admin.subscription.health.inactive": "غير نشط",
  "admin.subscription.modules.title": "الوحدات",
  "admin.subscription.modules.cloud": "وحدات السحابة",
  "admin.subscription.modules.cloud.hint": "وحدات مدفوعة — تُعلّق إذا لم تكن الحالة نشطة.",
  "admin.subscription.modules.local": "الوحدات المحلية",
  "admin.subscription.modules.local.hint": "حزم محلية — لا تُعلّق أبداً.",
  "admin.subscription.modules.empty": "لا توجد وحدات في هذه الفئة.",
  "admin.subscription.notSynced.title": "الاشتراك غير متزامن",
  "admin.subscription.notSynced.desc":
    "لم تتم مزامنة الاشتراك بعد من المركز. تبقى الميزات متاحة.",
  "admin.subscription.request.title": "طلب تغيير",
  "admin.subscription.request.cta": "طلب تغيير",
  "admin.subscription.request.type": "نوع الطلب",
  "admin.subscription.request.type.upgrade": "ترقية",
  "admin.subscription.request.type.downgrade": "تخفيض",
  "admin.subscription.request.type.cancel": "إلغاء",
  "admin.subscription.request.plan": "الخطة المستهدفة",
  "admin.subscription.request.plan.placeholder": "مثال: pro، premium",
  "admin.subscription.request.cycle": "الدورة",
  "admin.subscription.request.cycle.mensuel": "شهري",
  "admin.subscription.request.cycle.annuel": "سنوي",
  "admin.subscription.request.motif": "السبب (اختياري)",
  "admin.subscription.request.submit": "إرسال الطلب",
  "admin.subscription.request.planRequired": "يلزم خطة مستهدفة للترقية/التخفيض.",
  "admin.subscription.request.queued": "تم تسجيل الطلب — سيعالجه المركز.",
  "admin.subscription.request.reused": "الطلب موجود بالفعل في قائمة الانتظار.",
  "admin.subscription.request.notEnabled":
    "طلبات تغيير الاشتراك غير متاحة عبر الإنترنت في هذا التثبيت. تواصل مع مزوّد خدمة Pharmakon لتعديل خطتك.",
  "admin.subscription.request.note":
    "يقرر مركز Pharmakon؛ لا تُعدّل أي حقوق محلياً (ولا تُرسل أي بيانات للمريض).",
};

let registered = false;
export function registerAdminMessages(): void {
  if (registered) return;
  registered = true;
  registerMessages("fr", FR);
  registerMessages("ar", AR);
}
